/**
 * sessionApiService.js
 *
 * Thin client wrappers around the session management Cloud Functions.
 * Every call attaches the current user's Firebase ID token so the backend
 * can verify the caller without trusting any client-supplied UID.
 */

import { auth } from '../firebase';

const API_BASE =
  process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-auravit-bb843.cloudfunctions.net';

/**
 * Get a fresh Firebase ID token for the currently signed-in user.
 * Throws if no user is signed in.
 */
async function getIdToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user.');
  return user.getIdToken();
}

/**
 * Revoke a single session by its sessionId.
 * The backend verifies that the session belongs to the authenticated user.
 *
 * @param {string} sessionId
 * @returns {Promise<{ success: boolean }>}
 */
export async function apiRevokeSession(sessionId) {
  const idToken = await getIdToken();
  const res = await fetch(`${API_BASE}/revokeSession`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ sessionId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to revoke session.');
  return data;
}

/**
 * Revoke all sessions for the currently authenticated user.
 * This calls Firebase Admin revokeRefreshTokens() and marks every
 * session document as revoked.
 *
 * @returns {Promise<{ success: boolean, sessionsRevoked: number }>}
 */
export async function apiLogoutAllSessions() {
  const idToken = await getIdToken();
  const res = await fetch(`${API_BASE}/logoutAllSessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to log out all sessions.');
  return data;
}
